import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import assetService from '../services/assetService';
import AssetCard from '../components/AssetCard';
import StockCard from '../components/StockCard';
import MobileTopBarClean from '../components/MobileTopBarClean';
import { usePortfolioStats } from '../hooks/usePortfolioStats';
import '../styles/assets.css';
import '../styles/global-white-backgrounds.css';

const AssetsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { getHeaderStats } = usePortfolioStats();
  const [assets, setAssets] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const assetClasses = [
    { id: 'all', name: `🌟 ${t('assets:assetClasses.all.name')}`, color: '#6366f1', description: t('assets:assetClasses.all.description') },
    { id: 'stock', name: `📈 ${t('assets:assetClasses.stocks.name')}`, color: '#10b981', description: t('assets:assetClasses.stocks.description') },
    { id: 'bond', name: `🏦 ${t('assets:assetClasses.bondEtfs.name')}`, color: '#3b82f6', description: t('assets:assetClasses.bondEtfs.description') },
    { id: 'index', name: `📊 ${t('assets:assetClasses.indexEtfs.name')}`, color: '#8b5cf6', description: t('assets:assetClasses.indexEtfs.description') },
    { id: 'crypto', name: `🪙 ${t('assets:assetClasses.cryptoEtfs.name')}`, color: '#f59e0b', description: t('assets:assetClasses.cryptoEtfs.description') }
  ];

  useEffect(() => {
    // Check if coming from dashboard with specific category
    if (location.state?.category) {
      const categoryMap = {
        'stocks': 'stock',
        'etfs': 'index',
        'crypto': 'crypto',
        'bonds': 'bond'
      };
      setSelectedClass(categoryMap[location.state.category] || 'all');
    }
  }, [location.state]);

  useEffect(() => {
    loadAssets(selectedClass);
  }, [selectedClass]);

  const loadAssets = async (assetClass) => {
    setLoading(true);
    setError(null);
    
    try {
      // Map the asset class to service parameter
      let serviceParam = assetClass;
      if (assetClass === 'stock') {
        serviceParam = 'stocks'; // Service expects 'stocks' not 'stock'
      }
      
      // For stocks, get the list and then fetch price data
      if (assetClass === 'stock' || assetClass === 'all') {
        const result = await assetService.getAssets(serviceParam);
        const stocksWithPrices = [];
        
        // Get price data for first 20 stocks (to avoid rate limiting)
        const stocksToFetch = result.assets.slice(0, 20);
        
        for (const stock of stocksToFetch) {
          try {
            // Get previous day data for bid/ask (high/low)
            const priceResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${stock.ticker}/prev`);
            const priceData = await priceResponse.json();
            
            if (priceData.status === 'OK' && priceData.results?.[0]) {
              const dayData = priceData.results[0];
              stocksWithPrices.push({
                ...stock,
                assetClass: 'stock',
                day_high: dayData.h, // Ask price
                day_low: dayData.l,  // Bid price
                close: dayData.c,
                volume: dayData.v
              });
            }
          } catch (err) {
            // If price fetch fails, still show the stock
            stocksWithPrices.push({
              ...stock,
              assetClass: 'stock'
            });
          }
        }
        
        setAssets(stocksWithPrices);
      } else {
        // For other asset classes, use existing logic
        const result = await assetService.getAssets(serviceParam);
        const assetsWithClass = result.assets.map(asset => ({
          ...asset,
          assetClass: assetClass
        }));
        setAssets(assetsWithClass || []);
      }
    } catch (error) {
      console.error('Error loading assets:', error);
      setError('Failed to load assets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getAssetsByClass = () => {
    if (selectedClass === 'all') return assets;
    return assets.filter(asset => asset.assetClass === selectedClass);
  };

  return (
    <div className="assets-container">
      <MobileTopBarClean stats={getHeaderStats()} />

      {/* Header */}
      <div className="assets-header" style={{ marginTop: '75px' }}>
        <div className="header-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 className="assets-title" style={{ margin: 0 }}>{`🏪 ${t('assets:pages.assetsHub.title')}`}</h1>
          <div className="header-actions">
            <button className="portfolio-button" onClick={() => navigate('/dashboard')}>
              <span className="portfolio-icon">💼</span>
              {t('assets:pages.assetsHub.myPortfolio')}
            </button>
          </div>
        </div>
      </div>

      {/* Asset Class Tabs */}
      <section className="asset-class-tabs">
        <div className="tabs-container">
          {assetClasses.map(cls => (
            <button
              key={cls.id}
              className={`class-tab ${selectedClass === cls.id ? 'active' : ''}`}
              style={{
                '--tab-color': cls.color,
                borderColor: selectedClass === cls.id ? cls.color : 'transparent',
                background: selectedClass === cls.id ? `${cls.color}15` : 'transparent'
              }}
              onClick={() => setSelectedClass(cls.id)}
            >
              <span className="tab-name">{cls.name}</span>
              <span className="tab-description">{cls.description}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Main Content */}
      <main className="assets-main">
        {/* Category Header */}
        <div className="category-header">
          <h2 className="category-title">
            {assetClasses.find(c => c.id === selectedClass)?.name || `🌟 ${t('assets:assetClasses.all.name')}`}
          </h2>
          <p className="category-subtitle">
            {selectedClass === 'all'
              ? t('assets:categoryHeader.showing', { count: getAssetsByClass().length })
              : selectedClass === 'crypto'
                ? t('assets:categoryHeader.etfsAvailable', { count: getAssetsByClass().length })
                : selectedClass === 'stock'
                  ? t('assets:categoryHeader.stocksAvailable', { count: getAssetsByClass().length })
                  : t('assets:categoryHeader.fundsAvailable', { count: getAssetsByClass().length })
            }
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="loading-pokeball"></div>
            <p className="loading-text">{t('assets:loading.text')}</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="error-container">
            <span className="error-icon"></span>
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={() => loadAssets(selectedClass)}>
              Try Again
            </button>
          </div>
        )}

        {/* Assets Grid */}
        {!loading && !error && (
          <div className="assets-grid">
            {getAssetsByClass().length > 0 ? (
              getAssetsByClass().map(asset => {
                // Use StockCard for stocks, AssetCard for others
                if (asset.assetClass === 'stock') {
                  return <StockCard key={asset._id || asset.ticker} stock={asset} />;
                }
                return <AssetCard key={asset._id || asset.ticker} asset={asset} />;
              })
            ) : (
              <div className="no-assets">
                <span className="no-assets-icon">🔍</span>
                <p>No {selectedClass} investments found.</p>
              </div>
            )}
          </div>
        )}

        {/* Educational Tips */}
        <section className="tips-section">
          <div className="tips-container">
            <h3 className="tips-title">💡 Investment Tips</h3>
            <div className="tips-grid">
              <div className="tip-card">
                <span className="tip-icon">🎯</span>
                <h4>Diversify</h4>
                <p>Don't put all your money in one investment!</p>
              </div>
              <div className="tip-card">
                <span className="tip-icon">📊</span>
                <h4>Research</h4>
                <p>Learn about companies before investing</p>
              </div>
              <div className="tip-card">
                <span className="tip-icon">⏰</span>
                <h4>Be Patient</h4>
                <p>Good investments take time to grow</p>
              </div>
              <div className="tip-card">
                <span className="tip-icon">💰</span>
                <h4>Start Small</h4>
                <p>Begin with small amounts to learn</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Floating AI Assistant */}
    </div>
  );
};

export default AssetsPage;